<template>
  <!-- Desktop Gantt View -->
  <div v-if="!isMobileView" class="gantt-container">
    <div class="gantt-layout d-flex">
      <!-- Left Panel - Group Names -->
      <div class="groups-panel" :style="{ width: groupsPanelWidth + 'px' }">
        <div class="time-header-placeholder" :style="{ height: timeHeaderHeight + 'px' }"></div>
        <div
            v-for="group in groups"
            :key="group.id"
            class="group-row"
            :style="{ height: groupRowHeight + 'px' }"
        >
          <div class="group-name">{{ group.displayName }}</div>
        </div>
      </div>

      <!-- Right Panel - Timeline -->
      <div class="timeline-panel" ref="timelinePanel">
        <!-- Time Header -->
        <div class="time-header" :style="{ height: timeHeaderHeight + 'px' }">
          <div
              v-for="timeSlot in timeSlots"
              :key="timeSlot.timestamp"
              class="time-slot"
              :style="{
                width: timeSlot.width + 'px',
                minWidth: minTimeSlotWidth + 'px'
              }"
          >
            <div class="time-label">{{ timeSlot.label }}</div>
          </div>
        </div>

        <!-- Gantt Chart Area -->
        <div class="gantt-chart">
          <div
              v-for="group in groups"
              :key="group.id"
              class="gantt-row"
              :style="{ height: groupRowHeight + 'px' }"
          >
            <!-- Grid Lines -->
            <div class="grid-lines">
              <div
                  v-for="timeSlot in timeSlots"
                  :key="`grid-${group.id}-${timeSlot.timestamp}`"
                  class="grid-line"
                  :style="{
                    width: timeSlot.width + 'px',
                    minWidth: minTimeSlotWidth + 'px',
                    borderBottom: '1px solid #f0f0f0'
                  }"
              ></div>
            </div>

            <!-- Gantt Bars -->
            <div class="gantt-bars">
              <div
                  v-for="item in getItemsForGroup(group.id)"
                  :key="`bar-${item.row}-${item.start}-${item.text}`"
                  class="gantt-bar"
                  :class="`${item.color} ${(item.meta.id == props.focused?.id && item.meta.parent_id == props.focused?.order_id) ? 'highlightedBar' : ''}`"
                  :style="getBarStyle(item)"
                  @click="onItemClick(item)"
                  @mouseenter="showTooltip($event, item)"
                  @mouseleave="hideTooltip"
              >
              <!--{{ item.meta.id }} == {{props.focused.id}} {{ item.meta.parent_id == props.focused?.order_id }}-->
                <div class="bar-content">
                  <span class="bar-id">{{ item.text }}</span>
                  <span class="bar-title">{{ item.meta?.title || '' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>


  <!-- KOMPLETT NEUE Mobile View - KEINE ACCORDIONS! -->
  <div v-else class="mobile-view">
    <!-- Legend Header -->
    <div class="mobile-header pa-4">
      <div class="text-h6 mb-3">Gruppen-Legende</div>
      <div class="legend-row d-flex flex-wrap ga-3 mb-4">
        <div
            v-for="group in groups"
            :key="group.id"
            class="legend-chip d-flex align-center pa-2"
            :style="{ backgroundColor: 'bg-surface', borderColor: group.colorcode }"
        >
          <div
              class="legend-dot mr-2"
              :style="{ backgroundColor: group.colorcode }"
          ></div>
          <span class="text-caption font-weight-bold">{{ group.displayName }}</span>
        </div>
      </div>
    </div>

    <!-- Scrollable Timeline List -->
    <div class="mobile-timeline-list pa-4">
      <div class="text-h6 mb-4">Aufgaben-Timeline</div>

      <!-- Timeline Items -->
      <div class="timeline-flow">
        <div
            v-for="(item, index) in sortedTimelineItems"
            :key="`timeline-${item.row}-${item.start}-${item.text}`"
            class="timeline-card mb-3"
            @click="onItemClick(item)"
        >
          <!-- Time Badge -->
          <div class="time-badge mb-2">
            <v-icon size="16" class="mr-1">mdi-clock</v-icon>
            {{ formatTimeRange(item.start, item.end) }}
          </div>

          <!-- Task Card -->
          <v-card
              class="task-card elevation-2"
              :class="`${item.color}`"
              @click.stop="onItemClick(item)"
          >
            <v-card-text class="pa-3">
              <!-- Header Row -->
              <div class="d-flex justify-space-between align-center mb-2">
                <div class="d-flex align-center">
                  <v-chip
                      size="small"
                      variant="elevated"
                      class="task-id-chip mr-2"
                  >
                    {{ item.text }}
                  </v-chip>
                  <v-chip
                      :color="getGroupColor(item.row)"
                      size="x-small"
                      variant="flat"
                      class="group-chip"
                  >
                    {{ getGroupName(item.row) }}
                  </v-chip>
                </div>
                <div class="duration-info text-caption">
                  {{ formatDuration(item.start, item.end) }}
                </div>
              </div>

              <!-- Title -->
              <div class="task-title text-body-1 font-weight-bold mb-2">
                {{ item.meta?.title || 'Unbenannte Aufgabe' }}
              </div>

              <!-- Description -->
              <div
                  v-if="item.meta?.description"
                  class="task-description text-body-2 mb-3"
              >
                {{ item.meta.description }}
              </div>

              <!-- Progress Bar -->
              <div class="progress-section">
                <div class="progress-bar">
                  <div
                      class="progress-fill"
                      :style="{
                        width: getDurationPercentage(item) + '%'
                      }"
                  ></div>
                </div>
                <div class="text-caption mt-1 text-center">
                  {{ Math.round(getDurationPercentage(item)) }}% der Gesamtzeit
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Timeline Connector -->
          <div
              v-if="index < sortedTimelineItems.length - 1"
              class="timeline-connector"
          >
            <div class="connector-line"></div>
            <v-icon size="12" class="connector-arrow">mdi-arrow-down</v-icon>
          </div>
        </div>
      </div>

      <!-- Summary Footer -->
      <v-card class="summary-card mt-4" variant="outlined">
        <v-card-text class="pa-3">
          <div class="text-subtitle-2 mb-2 font-weight-bold">Zusammenfassung</div>
          <div class="d-flex justify-space-between mb-1">
            <span class="text-body-2">Gesamtdauer:</span>
            <span class="text-body-2 font-weight-bold">
                {{ formatDuration(props.scheduleData.min, props.scheduleData.max) }}
              </span>
          </div>
          <div class="d-flex justify-space-between mb-1">
            <span class="text-body-2">Anzahl Aufgaben:</span>
            <span class="text-body-2 font-weight-bold">{{ sortedTimelineItems.length }}</span>
          </div>
          <div class="d-flex justify-space-between">
            <span class="text-body-2">Zeitraum:</span>
            <span class="text-body-2 font-weight-bold" v-if="formatDate(props.scheduleData.min) == formatDate(props.scheduleData.max)">
                {{ formatDate(props.scheduleData.min) }}
              </span>
            <span class="text-body-2 font-weight-bold" v-else>
                {{ formatDate(props.scheduleData.min) }} - {{ formatDate(props.scheduleData.max) }}
              </span>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>

  <!-- Tooltip -->
  <teleport to="body">
    <div
        v-if="tooltip.show"
        ref="tooltipEl"
        class="gantt-tooltip"
        :style="tooltip.style"
    >
      <div class="tooltip-header">
        <span :class="`tooltip-id ${tooltip.item?.color}`">{{ tooltip.item?.text }}</span>
        <span class="tooltip-time">{{ tooltip.item ? formatTimeRange(tooltip.item.start, tooltip.item.end) : '' }}</span>
      </div>
      <div class="tooltip-title">{{ tooltip.item?.meta?.title || 'Kein Titel' }}</div>
      <div v-if="tooltip.item?.meta?.description" class="tooltip-description">
        {{ tooltip.item.meta.description }}
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import {computed, nextTick, onMounted, ref, toRef} from 'vue'
import {useDisplay} from 'vuetify'
import {TimelineItem, TimelineRow} from "@/types/VueScheduler";
import {Task, Group} from "@/types/Order";

interface ScheduleData {
  data: TimelineItem[]
  min: string
  max: string
}

interface Props {
  scheduleData: ScheduleData
  rows: TimelineRow[] | Group[]
  focused?: Task
}


const props = defineProps<Props>()

//console.log("focused", props.focused)

const emit = defineEmits<{
  itemClick: [item: TimelineItem]
}>()

const {mobile} = useDisplay()

// Reactive refs
const mouseX = ref(0);
const mouseY = ref(0);
const viewMode = ref<'desktop' | 'mobile'>('desktop')
const timelinePanel = ref<HTMLElement>()
const tooltipEl = ref<HTMLElement>()
const tooltip = ref<{
  show: boolean
  item: TimelineItem | null
  style: Record<string, any>
}>({
  show: false,
  item: null,
  style: {}
})

// Layout Constants
const groupsPanelWidth = 100
const timeHeaderHeight = 60
const groupRowHeight = 80
const minTimeSlotWidth = 50
const barHeight = groupRowHeight - 10

// Computed properties
const isMobileView = computed(() => {
  return viewMode.value === 'mobile' || mobile.value
})

const groups = toRef(props.rows)

const sortedTimelineItems = computed(() => {
  return [...props.scheduleData.data].sort((a, b) => {
    return new Date(a.start).getTime() - new Date(b.start).getTime()
  })
})

const timeRange = computed(() => {
  if (!props.scheduleData.min || !props.scheduleData.max) return []

  const start = new Date(props.scheduleData.min)
  const end = new Date(props.scheduleData.max)
  const times: Date[] = []

  const current = new Date(start)
  current.setMinutes(0, 0, 0)

  while (current <= end) {
    times.push(new Date(current))
    current.setMinutes(current.getMinutes() + 30)
  }

  return times
})

const availableWidth = computed(() => {
  /*if (!timelinePanel.value) return 800
  return timelinePanel.value.clientWidth || 800*/
  return 1200 - minTimeSlotWidth;
})


const timeSlotWidth = computed(() => {
  if (timeRange.value.length === 0) return minTimeSlotWidth
  const calculatedWidth = availableWidth.value / timeRange.value.length
  return Math.max(calculatedWidth, minTimeSlotWidth)
})

const timeSlots = computed(() => {
  return timeRange.value.map(time => ({
    timestamp: time.getTime(),
    label: time.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    }),
    width: timeSlotWidth.value
  }))
})

// Methods
const getItemsForGroup = (groupId: number): TimelineItem[] => {
  return props.scheduleData.data.filter(item => item.row === groupId)
}

const getGroupName = (rowId: number): string => {
  const group = groups.value.find(g => g.id === rowId)
  return group?.displayName || `Gruppe ${rowId}`
}

const getGroupColor = (rowId: number): string => {
  const group = groups.value.find(g => g.id === rowId)
  return group?.color || 'grey'
}

const getBarStyle = (item: TimelineItem): Record<string, string> => {
  if (!props.scheduleData.min) return {}

  const timelineStart = new Date(props.scheduleData.min)
  const itemStart = new Date(item.start)
  const itemEnd = new Date(item.end)

  const startOffsetHours = (itemStart.getTime() - timelineStart.getTime()) / (1000 * 60 * 60)
  const durationHours = (itemEnd.getTime() - itemStart.getTime()) / (1000 * 60 * 60)

  const left = startOffsetHours * 2 * timeSlotWidth.value
  //const width = (durationHours / totalDurationHours.value) * availableWidth.value
  const width = durationHours * 2 * timeSlotWidth.value

  const isHighlighted = item.meta.id == props.focused?.id && item.meta.orderId == props.focused?.order_id;

  return {
    position: 'absolute',
    left: `${left}px`,
    width: `${Math.max(width - 2, 50)}px`,
    height: `${barHeight}px`,
    top: `${(groupRowHeight - barHeight) / 2}px`,
    borderRadius: '6px',
    cursor: 'pointer',
  }
}

const formatTimeRange = (start: string, end: string): string => {
  const startTime = new Date(start).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  })
  const endTime = new Date(end).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  })
  return `${startTime} - ${endTime}`
}

const formatDuration = (start: string, end: string): string => {
  const startTime = new Date(start)
  const endTime = new Date(end)
  const durationMs = endTime.getTime() - startTime.getTime()
  const hours = Math.floor(durationMs / (1000 * 60 * 60))
  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`
  }
  return `${minutes}min`
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('de-DE')
}

const getDurationPercentage = (item: TimelineItem): number => {
  const itemDuration = new Date(item.end).getTime() - new Date(item.start).getTime()
  const totalDuration = new Date(props.scheduleData.max).getTime() - new Date(props.scheduleData.min).getTime()
  return Math.min((itemDuration / totalDuration) * 100, 100)
}

const onItemClick = (item: TimelineItem) => {
  emit('itemClick', item)
}

const showTooltip = (event: MouseEvent, item: TimelineItem) => {
  if (isMobileView.value) return

  tooltip.value.item = item
  tooltip.value.show = true
}

const hideTooltip = () => {
  tooltip.value.show = false
  tooltip.value.item = null
}

function updateMouse(e: MouseEvent) {
  mouseX.value = e.clientX;
  mouseY.value = e.clientY;
  if (tooltip.value.show) {
    nextTick(() => {
      tooltip.value.style = {
        position: 'fixed',
        left: `${mouseX.value + 10}px`,
        top: `${mouseY.value - 10}px`,
        transform: 'translateY(-100%)',
        zIndex: 9999
      }
    })
  }
}

// Lifecycle
onMounted(() => {
  if (mobile.value) {
    viewMode.value = 'mobile'
  }
  window.addEventListener('mousemove', updateMouse);
})
</script>

<style scoped>
.gantt-container {
  overflow: visible !important;
  border-top: 1px solid rgb(var(--v-theme-surface-variant));
  background: rgb(var(--v-theme-background));
}

.gantt-layout {
  min-height: 400px;
}

/* Desktop Styles */
.groups-panel {
  background: rgb(var(--v-theme-surface));
  flex-shrink: 0;
}

.time-header-placeholder {
  border-bottom: 2px solid rgb(var(--v-theme-surface-variant));
  background: rgb(var(--v-theme-surface-variant));
}

.group-row {
  border-bottom: 1px solid rgb(var(--v-theme-surface-variant));
  display: flex;
  align-items: center;
  padding: 0 16px;
  background: rgb(var(--v-theme-surface));
}

.group-name {
  font-weight: 600;
  font-size: 0.9rem;
  color: rgb(var(--v-theme-on-surface));
}

.timeline-panel {
  flex: 1;
  overflow-x: auto;
  overflow-y: visible !important;
  background: rgb(var(--v-theme-background));
  border-left: 2px solid rgb(var(--v-theme-surface-variant));
  margin-left: -2px;
  padding-left: 2px;
}

.time-header {
  display: flex;
  position: sticky;
  top: 0;
  z-index: 10;
}

.time-slot {
  background: rgb(var(--v-theme-surface-variant));
  border-bottom: 2px solid rgb(var(--v-theme-surface-variant));
  border-right: 1px solid rgb(var(--v-theme-outline-variant));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.time-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: rgb(var(--v-theme-on-surface-variant));
}

.gantt-chart {
  position: relative;
  overflow: visible !important;
}

.gantt-row {
  position: relative;
  overflow: visible !important;
  border-bottom: 1px solid rgb(var(--v-theme-surface-variant));
}

.grid-lines {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  pointer-events: none;
}

.grid-line {
  border-right: 1px solid rgb(var(--v-theme-outline-variant));
  border-bottom: 1px solid rgb(var(--v-theme-outline-variant));
  flex-shrink: 0;
}

.gantt-bars {
  position: relative;
  overflow: visible !important;
  height: 100%;
}

.gantt-bar {
  display: flex;
  align-items: center;
  padding: 0 12px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  width: 100%;
}

.gantt-bar:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.bar-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.bar-id {
  font-size: 0.75rem;
  font-weight: 700;
  opacity: 0.9;
  line-height: 1;
  color: rgba(var(--v-theme-text));
}

.bar-title {
  font-size: 0.7rem;
  font-weight: 500;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  margin-top: 2px;
  color: rgba(var(--v-theme-text));
}

/* Mobile Styles - Theme-aware */
.mobile-view {
  /*  background: rgb(var(--v-theme-background));*/
  min-height: 500px;
}

.mobile-header {
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgb(var(--v-theme-surface-variant));
}

.legend-row {
  gap: 8px;
}

.legend-chip {
  border: 1px solid;
  border-radius: 20px;
  min-width: 80px;
  justify-content: center;
  color: rgb(var(--v-theme-on-surface));
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.mobile-timeline-list {
  overflow-y: auto;
  background: rgb(var(--v-theme-background));
}

.timeline-flow {
  position: relative;
}

.timeline-card {
  position: relative;
}

.time-badge {
  display: flex;
  align-items: center;
  background: rgba(var(--v-theme-info), 0.3);
  color: rgb(var(--v-theme-text));
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 600;
  width: fit-content;
  margin-left: 8px;
}

.task-card {
  border-radius: 12px !important;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  /*  background: rgb(var(--v-theme-surface)) !important;*/
}

.task-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
  border-color: rgba(var(--v-theme-primary), 0.3);
}

.task-id-chip {
  background: rgba(var(--v-theme-surface-variant), 0.8) !important;
  color: rgb(var(--v-theme-on-surface-variant)) !important;
  font-weight: bold;
}

.group-chip {
  color: rgb(var(--v-theme-text));
}

.task-title {
  color: rgb(var(--v-theme-on-surface));
  line-height: 1.3;
}

.task-description {
  color: rgb(var(--v-theme-on-surface-variant));
  opacity: 0.9;
  line-height: 1.4;
}

.duration-info {
  color: rgb(var(--v-theme-on-surface-variant));
  opacity: 0.8;
  font-weight: 600;
}

.progress-section {
  margin-top: 12px;
}

.progress-bar {
  height: 6px;
  background: rgba(var(--v-theme-surface-variant), 0.5);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 4px;
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.8s ease;
  background: linear-gradient(90deg,
  rgba(var(--v-theme-primary), 0.6) 0%,
  rgba(var(--v-theme-primary), 0.9) 50%,
  rgba(var(--v-theme-primary), 0.6) 100%);
}

.timeline-connector {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  position: relative;
}

.connector-line {
  width: 2px;
  height: 16px;
  background: linear-gradient(to bottom,
  rgb(var(--v-theme-primary)),
  rgba(var(--v-theme-primary), 0.6));
  position: absolute;
}

.connector-arrow {
  background: rgb(var(--v-theme-surface)) !important;
  border-radius: 50%;
  color: rgb(var(--v-theme-primary)) !important;
  z-index: 2;
}

.summary-card {
  background: rgb(var(--v-theme-surface)) !important;
  border: 2px solid rgba(var(--v-theme-primary), 0.3) !important;
}

/* Background Colors für Gantt Bars */
.bg_color_pro-rot {
  background: linear-gradient(135deg, rgb(var(--v-theme-pro-rot)) 0%, rgba(var(--v-theme-pro-rot), 0.8) 100%) !important;
  color: white !important;
}

.bg_color_pro-hellblau {
  background: linear-gradient(135deg, rgb(var(--v-theme-pro-hellblau)) 0%, rgba(var(--v-theme-pro-hellblau), 0.8) 100%) !important;
  color: rgb(var(--v-theme-pro-dunkelblau)) !important;
}

.bg_color_pro-dunkelgrau {
  background: linear-gradient(135deg, rgb(var(--v-theme-pro-dunkelgrau)) 0%, rgba(var(--v-theme-pro-dunkelgrau), 0.8) 100%) !important;
  color: white !important;
}

.bg_color_pro-dunkelblau {
  background: linear-gradient(135deg, rgb(var(--v-theme-pro-dunkelblau)) 0%, rgba(var(--v-theme-pro-dunkelblau), 0.8) 100%) !important;
  color: white !important;
}

/* Tooltip - Theme-aware */
.gantt-tooltip {
  background: rgba(var(--v-theme-surface), 0.95);
  color: rgb(var(--v-theme-on-surface));
  border: 1px solid rgba(var(--v-theme-outline), 0.3);
  padding: 12px 16px;
  border-radius: 8px;
  max-width: 350px;
  pointer-events: none;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.tooltip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.tooltip-id {
  background: rgba(var(--v-theme-primary), 0.2);
  color: rgb(var(--v-theme-primary));
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.tooltip-time {
  font-size: 0.75rem;
  color: rgb(var(--v-theme-on-surface-variant));
  opacity: 0.8;
}

.tooltip-title {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 4px;
  line-height: 1.3;
  color: rgb(var(--v-theme-on-surface));
}

.tooltip-description {
  font-size: 0.8rem;
  color: rgb(var(--v-theme-on-surface-variant));
  opacity: 0.9;
  line-height: 1.4;
}

/* Responsive */
@media (max-width: 1200px) {
  .groups-panel {
    width: 120px;
  }

  .group-name {
    font-size: 0.8rem;
  }

  .time-label {
    font-size: 0.75rem;
  }

  .bar-title {
    display: none;
  }
}

@media (max-width: 960px) {
  .gantt-container {
    display: none;
  }

  .mobile-timeline-list {
    padding: 12px;
  }

  .legend-chip {
    min-width: 70px;
    font-size: 0.7rem;
  }
}

.highlightedBar {
  box-shadow: 0 0 0 0 white, 0 0 0 2px black !important;
}

</style>

<!--style scoped>
.gantt-container {
  overflow: hidden;
  border-top: 1px solid #e0e0e0;
}

.gantt-layout {
  min-height: 400px;
}

/* Desktop Styles */
.groups-panel {
  background: #fafafa;
  border-right: 2px solid #e0e0e0;
  flex-shrink: 0;
}

.time-header-placeholder {
  border-bottom: 2px solid #e0e0e0;
  background: #f5f5f5;
}

.group-row {
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  padding: 0 16px;
  background: #fafafa;
}

.group-name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #424242;
}

.timeline-panel {
  flex: 1;
  overflow-x: auto;
  background: white;
}

.time-header {
  display: flex;
  position: sticky;
  top: 0;
  z-index: 10;
}

.time-slot {
  background: #f5f5f5;
  border-bottom: 2px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.time-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #616161;
}

.gantt-chart {
  position: relative;
}

.gantt-row {
  position: relative;
  /*border-bottom: 1px solid #e0e0e0;*/
}

.grid-lines {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  pointer-events: none;
}

.grid-line {
  border-right: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.gantt-bars {
  position: relative;
  height: 100%;
}

.gantt-bar {
  display: flex;
  align-items: center;
  padding: 0 12px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  width: 100%;
}

.gantt-bar:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.bar-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.bar-id {
  font-size: 0.75rem;
  font-weight: 700;
  opacity: 0.9;
  line-height: 1;
}

.bar-title {
  font-size: 0.7rem;
  font-weight: 500;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  margin-top: 2px;
}

/* Mobile Styles - KOMPLETT NEU */
.mobile-view {
  background: #f8f9fa;
  min-height: 500px;
}

.mobile-header {
  background: white;
  border-bottom: 1px solid #e0e0e0;
}

.legend-row {
  gap: 8px;
}

.legend-chip {
  border: 1px solid;
  border-radius: 20px;
  min-width: 80px;
  justify-content: center;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.mobile-timeline-list {
  max-height: 70vh;
  overflow-y: auto;
  background: #f8f9fa;
}

.timeline-flow {
  position: relative;
}

.timeline-card {
  position: relative;
}

.time-badge {
  display: flex;
  align-items: center;
  background: #e3f2fd;
  color: #1565c0;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 600;
  width: fit-content;
  margin-left: 8px;
}

.task-card {
  border-radius: 12px !important;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.task-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
  border-color: rgba(255, 255, 255, 0.5);
}

.task-id-chip {
  background: rgba(255, 255, 255, 0.9) !important;
  color: inherit !important;
  font-weight: bold;
}

.group-chip {
  color: white !important;
}

.task-title {
  color: inherit;
  line-height: 1.3;
}

.task-description {
  color: inherit;
  opacity: 0.9;
  line-height: 1.4;
}

.duration-info {
  color: inherit;
  opacity: 0.8;
  font-weight: 600;
}

.progress-section {
  margin-top: 12px;
}

.progress-bar {
  height: 6px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 4px;
}

.progress-fill {
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 3px;
  transition: width 0.8s ease;
  background: linear-gradient(90deg,
  rgba(255, 255, 255, 0.6) 0%,
  rgba(255, 255, 255, 0.9) 50%,
  rgba(255, 255, 255, 0.6) 100%);
}

.timeline-connector {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  position: relative;
}

.connector-line {
  width: 2px;
  height: 16px;
  background: linear-gradient(to bottom, #2196f3, #64b5f6);
  position: absolute;
}

.connector-arrow {
  background: white;
  border-radius: 50%;
  color: #2196f3;
  z-index: 2;
}

.summary-card {
  background: white !important;
  border: 2px solid #e3f2fd !important;
}

/*!* Background Colors *!
.bg_color_pro-rot {
  background: linear-gradient(135deg, #f48fb1 0%, #e91e63 100%) !important;
  color: white !important;
}

.bg_color_pro-hellblau {
  background: linear-gradient(135deg, #81d4fa 0%, #2196f3 100%) !important;
  color: white !important;
}

.bg_color_pro-dunkelgrau {
  background: linear-gradient(135deg, #b0bec5 0%, #607d8b 100%) !important;
  color: white !important;
}*/

/* Tooltip */
.gantt-tooltip {
  background: rgba(33, 33, 33, 0.95);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  max-width: 350px;
  pointer-events: none;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.tooltip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.tooltip-id {
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.tooltip-time {
  font-size: 0.75rem;
  opacity: 0.8;
}

.tooltip-title {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 4px;
  line-height: 1.3;
}

.tooltip-description {
  font-size: 0.8rem;
  opacity: 0.9;
  line-height: 1.4;
}

/* Responsive */
@media (max-width: 1200px) {
  .groups-panel {
    width: 120px;
  }

  .group-name {
    font-size: 0.8rem;
  }

  .time-label {
    font-size: 0.75rem;
  }

  .bar-title {
    display: none;
  }
}

@media (max-width: 960px) {
  .gantt-container {
    display: none;
  }

  .mobile-timeline-list {
    padding: 12px;
  }

  .legend-chip {
    min-width: 70px;
    font-size: 0.7rem;
  }
}
</style-->